import { convertValueUnit } from '@metservicedev/cf-units';
import { Button } from 'antd';
import { Duration } from 'luxon';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DeleteOutlined } from '@ant-design/icons';
import { useMDSContext } from '@metservice/use-mds';
import { Capability } from '@mono/types/src/mds';

import allAggregateFunctions from '../../../utils/named-aggregate-functions';
import { generateMarks } from '../../../utils/generateMarks';
import { Carousel, Datability, FormWrapper, Select, Slider, Text } from '../../../WidgetConfiguratorComponents/FormGroups';
import { WarningBoxConfigBlockState, IWarning } from '../types';
import IDatability from 'WidgetBlocks/types/IDatability';

const DEFAULT_ALERT_AGGREGATE = 'maximum';
const DEFAULT_ALERT_LABEL = 'Highest';
const DEFAULT_ALERT_PERIOD_MINUTES = 60 * 24;
const DEFAULT_DP = 2;

const getTimeRange = (minutes: number) => {
    const startTime = Duration.fromObject({ minute: 0 }).toISO();
    const endTime = Duration.fromObject({ minute: minutes }).toISO();

    return `${startTime}--${endTime}`;
};

interface DataSettingsConfiguratorProps {
    viewblockState: WarningBoxConfigBlockState;
    setViewblockState: React.Dispatch<React.SetStateAction<WarningBoxConfigBlockState>>;
    baseDatability: any;
}

const DataSettingsConfigurator = ({ viewblockState, setViewblockState, baseDatability }: DataSettingsConfiguratorProps) => {
    const [isHideAddButton, setIsHideAddButtion] = useState(false);
    const { capabilities } = useMDSContext();
    const { warnings } = viewblockState.settings;

    const getVariableUnit = (capability: Capability) => {
        const capabilityVariables = Object.keys(capability.variables);
        const variables = capability.variables[capabilityVariables[0]];
        if (!variables) return null;

        return variables.units;
    };

    const onWarningSettingsChange = (key: string, value: any, warningId: string) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: (prev.settings.warnings ?? []).map((w) => {
                    if (w.id !== warningId) {
                        return w;
                    }

                    return {
                        ...w,
                        [key]: value,
                    };
                }),
            },
        }));
    };

    const onAlertSettingsChange = (key: string, value: any, warningId: string) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: (prev.settings.warnings ?? []).map((w) => {
                    if (w.id !== warningId) return w;
                    if (!w.alert) return w;

                    const aggregateLabel = key === 'aggregate' ? '' : w.alert.label;
                    console.log(aggregateLabel);

                    return {
                        ...w,
                        alert: {
                            ...w.alert,
                            label: aggregateLabel,
                            [key]: value,
                        },
                    };
                }),
            },
        }));
    };

    const onAddDataWarningDefinition = () => {
        const newDatability = {
            ...baseDatability,
            id: uuid(),
            capabilityId: null,
            datasetId: null,
            timeRange: getTimeRange(DEFAULT_ALERT_PERIOD_MINUTES),
        };

        const newWarning = {
            id: uuid(),
            databilityId: newDatability.id,
            dp: DEFAULT_DP,
            unit: '',
            label: '',
            alert: {
                aggregate: DEFAULT_ALERT_AGGREGATE,
                label: DEFAULT_ALERT_LABEL,
                periodMinutes: DEFAULT_ALERT_PERIOD_MINUTES,
            },
        };

        setIsHideAddButtion((viewblockState.settings.warnings ?? []).length >= 3 ? true : false);
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: [...(prev.settings.warnings ?? []), newWarning],
            },
            databilities: [...prev.databilities, newDatability],
        }));
    };

    const onDeleteWarning = (warningId: string) => {
        const warning = (viewblockState.settings.warnings ?? []).find((w) => w.id === warningId);
        if (!warning) return;

        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: (prev.settings.warnings ?? []).filter((w) => w.id !== warningId),
                thresholds: (prev.settings.thresholds ?? []).filter((t) => t.warningId !== warningId),
            },
            databilities: prev.databilities.filter((d) => d.id !== warning.databilityId),
        }));

        setIsHideAddButtion((viewblockState.settings.warnings ?? []).length >= 3 ? true : false);
    };

    const onUnitChange = ({ datability, unit }: { datability: IDatability; unit: string }) => {
        setViewblockState((prev) => {
            const newState = {
                ...prev,
                settings: {
                    ...prev.settings,
                    warnings: (prev.settings.warnings ?? []).map((w) => {
                        if (w.databilityId !== datability.id) {
                            return w;
                        }
                        return {
                            ...w,
                            unit: unit,
                            unitLabel: unit,
                        };
                    }),
                },
            };

            // Get all the thresholds of the warning
            // Check the variableunit is the same as the warning unit. if not then do the conversion
            const capability = capabilities.find((c) => c.id === datability.capabilityId);

            if (!capability) return prev;

            const warning = (prev.settings.warnings ?? []).find((w) => w.databilityId === datability.id);
            if (!warning) return prev;
            const capabilityVariableUnit = getVariableUnit(capability);
            if (!capabilityVariableUnit) return prev;
            const warningThresholds = (prev.settings.thresholds ?? []).filter((threshold) => threshold.warningId === warning.id);

            // Convert the values in threshold
            if (warningThresholds.length > 0 && capabilityVariableUnit !== unit) {
                return {
                    ...newState,
                    settings: {
                        ...newState.settings,
                        thresholds: (newState.settings.thresholds ?? []).map((threshold) => {
                            if (threshold.warningId === warning.id) {
                                return {
                                    ...threshold,
                                    value: convertValueUnit({ from: capabilityVariableUnit, to: unit, value: threshold.value }),
                                };
                            }
                            return threshold;
                        }),
                    },
                };
            } else {
                return newState;
            }
        });
    };

    const onInterpolationChange = (databilityId: string, interpolationInterval: string) => {
        setViewblockState((prev) => ({
            ...prev,
            databilities: prev.databilities.map((datability) => {
                if (datability.id === databilityId) {
                    return {
                        ...datability,
                        interpolationInterval,
                    };
                }

                return datability;
            }),
        }));
    };

    const onChangeTimeRange = (minutes: number, warning: IWarning) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: (prev.settings.warnings ?? []).map((w) => {
                    if (w.id !== warning.id) {
                        return w;
                    }
                    if (!w.alert) return w;

                    return {
                        ...w,
                        alert: {
                            ...w.alert,
                            periodMinutes: minutes,
                        },
                    };
                }),
            },
            databilities: prev.databilities.map((datability) => {
                if (datability.id !== warning.databilityId) return datability;
                return {
                    ...datability,
                    timeRange: getTimeRange(minutes),
                };
            }),
        }));
    };

    const onChangeDatability = (newDatability: IDatability) => {
        setViewblockState((prev) => {
            const { capabilityId, id } = newDatability;
            const warning = (prev.settings.warnings ?? []).find((warning) => warning.databilityId === id);
            if (!warning) return prev;

            return {
                ...prev,
                databilities: prev.databilities.map((datability) => (datability.id === newDatability.id ? newDatability : datability)),
                warnings: (prev.settings.warnings ?? []).map((w) => {
                    if (w.id !== warning.id) {
                        return w;
                    }
                    if (!capabilityId)
                        return {
                            ...w,
                            label: '',
                            unit: null,
                            unitLabel: null,
                        };

                    const capability = capabilities.find((c) => c.id === capabilityId);
                    if (!capability) return w;
                    const units = getVariableUnit(capability);

                    return {
                        ...w,
                        label: capability.display_name_short,
                        unit: units,
                        unitLabel: units,
                    };
                }),
            };
        });
    };

    const onUnitLabelChange = ({ warningId, unitLabel }: { warningId: string; unitLabel: string }) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                warnings: (warnings ?? []).map((warning) => {
                    if (warning.id !== warningId) return warning;

                    return { ...warning, unitLabel };
                }),
            },
        }));
    };

    return (
        <FormWrapper>
            <Carousel label='Warning Data Source' addItem={onAddDataWarningDefinition} hideAdd={isHideAddButton}>
                {(viewblockState.settings.warnings ?? []).map((warning, warningIndex) => {
                    const datability = viewblockState.databilities.find(({ id }) => id === warning.databilityId);
                    return (
                        <React.Fragment key={`warning-data-source-${warningIndex}`}>
                            {datability && (
                                <Datability
                                    onChangeDatability={onChangeDatability}
                                    unit={warning.unit}
                                    unitLabel={warning.unitLabel}
                                    onUnitLabelChange={(unitLabel) => onUnitLabelChange({ unitLabel, warningId: warning.id })}
                                    dataType={[`scalar`]}
                                    datability={datability}
                                    onInterpolationChange={(interpolationInterval) => onInterpolationChange(warning.databilityId, interpolationInterval)}
                                    onUnitChange={onUnitChange}
                                />
                            )}
                            <Text
                                label='Monitor Label'
                                value={warning.label}
                                onChange={(warningLabel) => onWarningSettingsChange('label', warningLabel, warning.id)}
                            />
                            {warning.alert && (
                                <Select
                                    label='Aggregation Algorithm'
                                    value={warning.alert.aggregate}
                                    values={Object.keys(allAggregateFunctions)}
                                    onChange={(aggregateFc: string) => onAlertSettingsChange('aggregate', aggregateFc, warning.id)}
                                />
                            )}
                            {warning.alert && (
                                <Text
                                    label='Aggregation Label'
                                    value={warning.alert.label}
                                    onChange={(alertLabel) => onAlertSettingsChange('label', alertLabel, warning.id)}
                                />
                            )}
                            <Slider
                                label='Decimal Point (if any)'
                                onChange={(dp) => onWarningSettingsChange('dp', dp, warning.id)}
                                value={warning.dp ?? 0}
                                min={0}
                                max={4}
                                marks={generateMarks([0, 1, 2, 3, 4])}
                            />
                            {warning.alert && (
                                <Slider
                                    label='Time range'
                                    onChange={(periodMinutes) => onChangeTimeRange(periodMinutes * 60, warning)}
                                    value={warning.alert.periodMinutes / 60}
                                    min={1}
                                    max={72}
                                    marks={generateMarks([1, 6, 12, 24, 48, 72])}
                                />
                            )}
                            <div style={{ gridColumn: '1 / 6', textAlign: 'right' }}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => onDeleteWarning(warning.id)}>
                                    Delete&nbsp;<strong>{warning.label}</strong>
                                </Button>
                            </div>
                        </React.Fragment>
                    );
                })}
            </Carousel>
        </FormWrapper>
    );
};

export default DataSettingsConfigurator;
